import React, { useEffect, useRef } from 'react';
import { LineChart, Path } from 'react-native-svg-charts';
import { Circle, Line, Defs, ClipPath, Rect, G } from 'react-native-svg';
import { View, Text } from 'react-native';
import moment from 'moment';
import * as shape from 'd3-shape';

import { SmallLoading } from '@components/loading';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import { isPhone, isWeb, localTime } from '@helpers';

import { getLineLegend, getValuesLine } from '@components/ncFiles/components/eventsChart/calculation';
import { MachinesScreen } from '@v2/pages/machines/common/enums';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { DATE_FORMAT } from '@v2/pages/common/constants/base.constants';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';

const LineVertical = (props) => {
  const { x, y, data, displayIndex, C } = props;
  return (
    <>
      <Line y1={y(data[1].data[displayIndex])} y2={'100%'} x1={x(displayIndex)} x2={x(displayIndex)} stroke={C('Gray/Light/4')} strokeWidth={2} />
    </>
  );
};

const Dots = (props) => {
  const { x, y, data, displayIndex, C } = props;
  return (
    <>
      {data.map((i) =>
        i.data.map((value, index) => {
          return (i.showFullLine ? index === i.data?.length - 1 : index === displayIndex) ? (
            <Circle key={index} cx={x(index)} cy={y(value)} r={7} stroke={C('Background/Light/Field')} strokeWidth={scale(3)} fill={i.svg.stroke} />
          ) : null;
        })
      )}
    </>
  );
};

const DashedLine = (props) => {
  const { lines, C } = props;
  return (
    <>
      <Path d={lines[1]} stroke={C('Background/Light/Field')} strokeWidth={scale(4)} fill={'none'} clipPath={'url(#clip-path-2)'} />
      <Path d={lines[0]} stroke={C('Gray/Light/3')} strokeWidth={scale(3)} fill={'none'} clipPath={'url(#clip-path-2)'} />
    </>
  );
};

const Clips = ({ x, width, displayIndex }) => (
  <Defs key={'clips'}>
    <ClipPath id="clip-path-1">
      <Rect x={'0'} y={'0'} width={x(displayIndex)} height={'100%'} />
    </ClipPath>
    <ClipPath id={'clip-path-2'}>
      <Rect x={x(displayIndex)} y={'0'} width={width - x(displayIndex)} height={'100%'} />
    </ClipPath>
  </Defs>
);

const LineChartComponent = ({
  C,
  fullWidth,
  data,
  dataPrev,
  isActive,
  id,
  selectedChartDate,
  setDataPrev,
  setData,
  setNcFiles,
  getNcFileStatsDay,
  getNcFileStatsHour,
  sendLog,
  chartTitle,
}) => {
  const abortControllerLine = useRef();
  const { timePeriod: selectedTime } = useHandleParams();

  useEffect(() => {
    if (abortControllerLine.current) {
      abortControllerLine.current.abort();
    }

    abortControllerLine.current = new AbortController();
    const signal = abortControllerLine.current.signal;

    (async () => {
      let momentStart = localTime();
      let momentEnd = localTime();
      if (selectedChartDate) {
        momentStart = localTime(selectedChartDate);
        momentEnd = localTime(selectedChartDate);
      }

      setDataPrev({});
      setNcFiles([]);
      const performanceStorage = {};

      switch (selectedTime) {
        case 'day': {
          const start = momentStart.clone().startOf('day');
          const end = momentEnd.clone().add(1, 'day').startOf('day');
          const startPrev = momentStart.clone().subtract(1, 'week').startOf('day');
          const endPrev = momentEnd.clone().subtract(1, 'week').add(1, 'day').startOf('day');

          let t1 = performance.now();
          const data = await getNcFileStatsHour({ id, start: start.format(DATE_FORMAT), end: end.format(DATE_FORMAT) }, signal);
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          t1 = performance.now();
          const dataPrev = await getNcFileStatsHour({ id, start: startPrev.format(DATE_FORMAT), end: endPrev.format(DATE_FORMAT) }, signal);
          t2 = performance.now();
          performanceStorage.secondAPIcall = t2 - t1;
          performanceStorage.firstAmount = data?.length;
          performanceStorage.secondAmount = dataPrev?.length;

          t1 = performance.now();
          const info = getValuesLine({ start: start.format(), data, amount: 24, period: 'hour' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;

          t1 = performance.now();
          const infoPrev = getValuesLine({ start: startPrev.format(), data: dataPrev, amount: 24, period: 'hour' });
          t2 = performance.now();
          performanceStorage.secondCalculation = t2 - t1;

          setDataPrev({
            ...infoPrev,
            selectedTime,
            start: startPrev.format(DATE_FORMAT),
            end: endPrev.format(DATE_FORMAT),
          });

          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });

          break;
        }
        case 'week': {
          const start = momentStart.clone().startOf('isoWeek');
          const end = momentEnd.clone().add(1, 'isoWeek').startOf('isoWeek');
          const startPrev = momentStart.clone().subtract(1, 'isoWeek').startOf('isoWeek');
          const endPrev = start.clone();

          let t1 = performance.now();
          const dataFull = await getNcFileStatsHour({ id, start: startPrev.format(DATE_FORMAT), end: end.format(DATE_FORMAT) }, signal);
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          let index = 0;
          dataFull.some((e, i) => {
            index = i;
            return localTime(e.dateTime).isAfter(endPrev);
          });
          const dataPrev = dataFull.slice(0, index);
          const data = dataFull.slice(index);

          performanceStorage.firstAmount = data?.length;
          performanceStorage.secondAmount = dataPrev?.length;

          t1 = performance.now();
          const info = getValuesLine({ start: start.format(), data, amount: 7 * 24, period: 'hour' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;

          t1 = performance.now();
          const infoPrev = getValuesLine({ start: startPrev.format(), data: dataPrev, amount: 7 * 24, period: 'hour' });
          t2 = performance.now();
          performanceStorage.secondCalculation = t2 - t1;

          setDataPrev({
            ...infoPrev,
            selectedTime,
            start: startPrev.format(DATE_FORMAT),
            end: endPrev.format(DATE_FORMAT),
          });

          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });
          break;
        }
        case 'month': {
          const start = momentStart.clone().startOf('month');
          const end = momentEnd.clone().add(1, 'month').startOf('month');
          const amount = momentStart.daysInMonth();
          const startPrev = momentStart.clone().subtract(1, 'month').startOf('month');
          const endPrev = start.clone();
          const amountPrev = momentStart.clone().subtract(1, 'month').daysInMonth();

          let t1 = performance.now();
          const dataFull = await getNcFileStatsDay({ id, start: startPrev.format(DATE_FORMAT), end: end.format(DATE_FORMAT) }, signal);
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          let index = 0;
          dataFull.some((e, i) => {
            index = i;
            return localTime(e.date).isAfter(endPrev);
          });
          const dataPrev = dataFull.slice(0, index);
          const data = dataFull.slice(index);

          performanceStorage.firstAmount = data?.length;
          performanceStorage.secondAmount = dataPrev?.length;

          t1 = performance.now();
          const info = getValuesLine({ start: start.format(), data, amount, period: 'day' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;

          t1 = performance.now();
          const infoPrev = getValuesLine({ start: startPrev.format(), data: dataPrev, amount: amountPrev, period: 'day' });
          t2 = performance.now();
          performanceStorage.secondCalculation = t2 - t1;

          setDataPrev({
            ...infoPrev,
            selectedTime,
            start: startPrev.format(DATE_FORMAT),
            end: endPrev.format(DATE_FORMAT),
          });

          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });
          break;
        }
        case 'year': {
          const start = momentStart.clone().startOf('year');
          const end = momentEnd.clone().add(1, 'year').startOf('year');
          const amount = momentStart.clone().endOf('year').dayOfYear();
          const startPrev = momentStart.clone().subtract(1, 'year').startOf('year');
          const endPrev = start.clone();
          const amountPrev = momentStart.clone().subtract(1, 'year').endOf('year').dayOfYear();

          let t1 = performance.now();
          const dataFull = await getNcFileStatsDay({ id, start: startPrev.format(DATE_FORMAT), end: end.format(DATE_FORMAT) }, signal);
          let t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;

          let index = 0;
          dataFull.some((e, i) => {
            index = i;
            return localTime(e.date).isAfter(endPrev);
          });
          const dataPrev = dataFull.slice(0, index);
          const data = dataFull.slice(index);

          performanceStorage.firstAmount = data?.length;
          performanceStorage.secondAmount = dataPrev?.length;

          t1 = performance.now();
          const info = getValuesLine({ start: start.format(), data, amount, period: 'day' });
          t2 = performance.now();
          performanceStorage.firstCalculation = t2 - t1;

          t1 = performance.now();
          const infoPrev = getValuesLine({ start: startPrev.format(), data: dataPrev, amount: amountPrev, period: 'day' });
          t2 = performance.now();
          performanceStorage.secondCalculation = t2 - t1;

          setDataPrev({
            ...infoPrev,
            selectedTime,
            start: startPrev.format(DATE_FORMAT),
            end: endPrev.format(DATE_FORMAT),
          });

          setData({
            ...info,
            selectedTime,
            start: start.format(DATE_FORMAT),
            end: end.format(DATE_FORMAT),
          });
          break;
        }
      }
      sendLog({ title: LOGGER_TITLES.LINE_CHART_PERFORMANCE, data: { ...performanceStorage, selectedTime, machine: id }, status: 'debug' });
    })();
  }, [selectedTime, id, selectedChartDate]);

  const displayDataNow = (isActive ? data.dataLineActive : data.dataLineProductive) || [];
  const displayDataOld = (isActive ? dataPrev.dataLineActive : dataPrev.dataLineProductive) || [];

  const availableWidth = isPhone ? fullWidth - scale(30) : scale(480, false);

  if (!displayDataNow?.length || !displayDataOld?.length || !data.selectedTime) {
    return (
      <View style={{ width: availableWidth }}>
        <View style={{ marginTop: scale(70) }}>
          <SmallLoading />
        </View>
      </View>
    );
  }
  const showFullInfo = moment.utc().isAfter(moment.utc(data.end));

  const { legendOffset, bigLegend, colorDots, colorWhiteDots, displayIndex, legendItems, currentLegend } = getLineLegend({
    selectedTime: data.selectedTime,
    start: data.start,
    end: data.end,
    showFullInfo,
  });

  const offset = scale(6);

  const color = {
    [MachinesScreen.ACTIVE]: C('Status/Light/Active'),
    [MachinesScreen.PRODUCTIVE]: C('Status/Light/Productive'),
  };

  const dataFormatted = [
    {
      data: displayDataOld,
      svg: { stroke: C('Gray/Light/3'), strokeWidth: scale(3) },
      showFullLine: true,
    },
    {
      data: displayDataNow,
      svg: { stroke: color[chartTitle], strokeWidth: scale(3) },
      showFullLine: false,
    },
  ];

  const chartWidth = availableWidth - 2 * offset;
  return (
    <View style={{ width: availableWidth, marginTop: scale(4) }}>
      <LineChart
        style={{ height: scale(165), width: availableWidth }}
        data={dataFormatted}
        contentInset={{
          left: offset,
          right: offset,
          bottom: offset,
          top: offset,
        }}
        curve={shape.curveMonotoneX}
      >
        {!showFullInfo && <Clips {...{ displayIndex, C }} />}
        {!showFullInfo && <DashedLine {...{ displayIndex, C }} />}
        {!showFullInfo && <LineVertical {...{ displayIndex, C }} />}
        {!showFullInfo && <Dots {...{ displayIndex, C }} />}
      </LineChart>
      <View
        style={{
          width: '100%',
          height: scale(7),
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingHorizontal: offset,
        }}
      >
        {legendItems.map((i, index) => {
          const isBig = bigLegend.includes(i);
          const isColor = colorDots.includes(i);
          const isWhiteColor = colorWhiteDots.includes(i);
          const isFirst = index === 0;
          const isLast = index === legendItems?.length - 1;
          const isCurrent = currentLegend === i;

          let textWidth = scale(9.1 * i?.length || 0, false);

          const totalItem = legendItems?.length - 1;

          const hide =
            !isCurrent &&
            !showFullInfo &&
            ((index === 0 && displayIndex < legendOffset) || (index === totalItem && displayIndex > totalItem - legendOffset));

          return (
            <View
              style={{
                width: chartWidth / legendItems?.length,
                maxWidth: scale(2),
                height: scale(2),
                borderRadius: scale(2) / 2,
                backgroundColor: isWhiteColor ? C('Gray/Light/4') : isColor ? C('Gray/Light/1') : 'transparent',
                position: isWeb ? 'initial' : 'relative',
              }}
              key={`${i}-${index}`}
            >
              {isBig && !hide && (
                <Text
                  numberOfLines={1}
                  style={[
                    font['Footnote'],
                    {
                      color: C('Label/Light/Quaternary'),
                      position: 'absolute',
                      top: scale(3),
                      width: textWidth,
                    },
                    isFirst
                      ? {}
                      : isCurrent &&
                        (isWeb
                          ? {
                              textAlign: 'center',
                              marginLeft: -textWidth / 2,
                            }
                          : {
                              right: -scale(textWidth) / 2,
                              textAlign: 'center',
                            }),
                    isLast && {
                      right: scale(0),
                      textAlign: 'right',
                      marginRight: isWeb ? offset : 0,
                    },
                    isFirst && {
                      textAlign: 'left',
                    },
                  ]}
                >
                  {i}
                </Text>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );
};

export default LineChartComponent;
